import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, formatDateBR } from '../api'
import type { ReportStats, ReportSummary } from '../api'

// Relatórios: busca full-text nos laudos, filtros e estatísticas agregadas
// — tudo consultado no banco local (offline).
export function Relatorios({ onOpenReport }: { onOpenReport: (id: string) => void }) {
  const [tab, setTab] = useState<'laudos' | 'stats'>('laudos')
  const [q, setQ] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [type, setType] = useState('')
  const [items, setItems] = useState<ReportSummary[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<ReportStats | null>(null)
  const [types, setTypes] = useState<string[]>([])
  const [error, setError] = useState('')

  const search = useCallback(async () => {
    setError('')
    try {
      const params: Record<string, string> = { limit: '200' }
      if (q) params.q = q
      if (from) params.from = from
      if (to) params.to = to
      if (type) params.type = type
      const out = await api.reports.list(params)
      setItems(out.items)
      setTotal(out.total)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha na busca')
    }
  }, [q, from, to, type])

  useEffect(() => { void search() }, [search])
  useEffect(() => { void api.templates.list().then((t) => setTypes(t.items.map((x) => x.name))).catch(() => undefined) }, [])
  useEffect(() => {
    if (tab !== 'stats') return
    void api.reports.stats(from, to).then(setStats).catch(() => setError('falha ao carregar estatísticas'))
  }, [tab, from, to])

  const grouped = items.reduce<Record<string, ReportSummary[]>>((acc, r) => {
    ;(acc[r.model] ??= []).push(r)
    return acc
  }, {})

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', minHeight: 0 }}>
      {/* filtros */}
      <div style={{ width: 258, flex: 'none', borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 'none', padding: '9px 12px', borderBottom: '1px solid var(--border)', font: '600 10.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-2)' }}>
          Painel de filtros
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 11, minHeight: 0 }}>
          <label className="field"><span>Palavras no laudo</span>
            <input className="input" placeholder="ex.: pólipo OR divertículo" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="field"><span>Período (data do exame)</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ flex: 1, fontFamily: 'var(--font-mono)' }} />
              <span style={{ color: 'var(--text-3)', fontSize: 11 }}>até</span>
              <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ flex: 1, fontFamily: 'var(--font-mono)' }} />
            </div>
          </div>
          <label className="field"><span>Tipo de laudo</span>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Todos</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <div style={{ flex: 'none', padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: 7 }}>
          <button className="btn btn-primary" onClick={() => void search()} style={{ flex: 1, height: 32 }}>Pesquisar</button>
          <button className="btn" onClick={() => { setQ(''); setFrom(''); setTo(''); setType('') }} style={{ height: 32 }}>Limpar</button>
        </div>
      </div>

      {/* resultados */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, background: 'var(--surface)' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'stretch', padding: '0 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <button data-active={tab === 'laudos' ? '1' : '0'} onClick={() => setTab('laudos')}
            style={{ padding: '9px 16px', border: 'none', borderRight: '1px solid var(--border)', font: '600 12px/1 var(--font-sans)', cursor: 'pointer', background: tab === 'laudos' ? 'var(--surface)' : 'transparent' }}>
            Laudos ({total})
          </button>
          <button data-active={tab === 'stats' ? '1' : '0'} onClick={() => setTab('stats')}
            style={{ padding: '9px 16px', border: 'none', borderRight: '1px solid var(--border)', font: '600 12px/1 var(--font-sans)', cursor: 'pointer', background: tab === 'stats' ? 'var(--surface)' : 'transparent' }}>
            Estatísticas
          </button>
        </div>

        {error && <div style={{ padding: '8px 12px', background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12 }}>{error}</div>}

        {tab === 'laudos' && (
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '82px 84px 1.4fr 90px 1.2fr 1.1fr 1.1fr', padding: '7px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', position: 'sticky', top: 0, font: '600 9.5px/1.2 var(--font-sans)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
              <div>Data</div><div>Laudo Nº</div><div>Paciente</div><div>Status</div><div>Examinador</div><div>Convênio</div><div>Trecho encontrado</div>
            </div>
            {Object.entries(grouped).map(([model, rows]) => (
              <div key={model}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--primary-soft)', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ font: '600 11.5px/1 var(--font-sans)', color: 'var(--primary)' }}>{model}</span>
                  <span style={{ font: '400 10.5px/1 var(--font-mono)', color: 'var(--text-2)' }}>({rows.length})</span>
                </div>
                {rows.map((r) => (
                  <div key={r.id} onClick={() => onOpenReport(r.id)} className="fade-in"
                    style={{ display: 'grid', gridTemplateColumns: '82px 84px 1.4fr 90px 1.2fr 1.1fr 1.1fr', padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', alignItems: 'center', font: '400 11.5px/1.3 var(--font-sans)' }}>
                    <div style={{ fontFamily: 'var(--font-mono)' }}>{formatDateBR(r.examDate)}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{r.number}</div>
                    <div style={{ fontWeight: 500 }}>{r.patientName}</div>
                    <div>
                      <span className="badge" style={{ background: r.status === 'liberado' ? 'var(--ok-soft)' : 'var(--accent-soft)', color: r.status === 'liberado' ? 'var(--ok)' : 'var(--accent)' }}>
                        {r.status === 'liberado' ? 'LIBERADO' : 'RASCUNHO'}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-2)' }}>{r.professionalName || '—'}</div>
                    <div style={{ color: 'var(--text-2)' }}>{r.insurance || '—'}</div>
                    <div style={{ color: 'var(--accent)' }}>{highlight(r, q)}</div>
                  </div>
                ))}
              </div>
            ))}
            {items.length === 0 && (
              <div style={{ padding: '40px 16px', textAlign: 'center', font: '400 12px/1.7 var(--font-mono)', color: 'var(--text-3)' }}>
                Nenhum laudo encontrado para estes filtros.
              </div>
            )}
          </div>
        )}

        {tab === 'stats' && stats && (
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '14px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
              {[
                { k: 'Total de laudos', v: stats.total, s: from || to ? 'no período filtrado' : 'em toda a base' },
                { k: 'Liberados', v: stats.released, s: stats.total ? `${Math.round((stats.released / stats.total) * 100)}% do total` : '—' },
                { k: 'Rascunhos', v: stats.draft, s: 'aguardando liberação' },
                { k: 'Tipos de exame', v: stats.byType.length, s: stats.byType[0] ? `mais frequente: ${stats.byType[0].label}` : '—' },
              ].map((c) => (
                <div key={c.k} className="card" style={{ padding: '11px 13px', background: 'var(--surface-2)' }}>
                  <div style={{ font: '500 9.5px/1 var(--font-sans)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{c.k}</div>
                  <div style={{ marginTop: 6, font: '600 24px/1 var(--font-sans)' }}>{c.v}</div>
                  <div style={{ marginTop: 4, font: '400 10.5px/1.3 var(--font-mono)', color: 'var(--text-2)' }}>{c.s}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 12 }}>
              <div className="card" style={{ padding: '13px 15px', background: 'var(--surface-2)' }}>
                <div style={{ font: '600 12px/1 var(--font-sans)' }}>Laudos por tipo de exame</div>
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <BarList items={stats.byType} />
                </div>
              </div>
              <div className="card" style={{ padding: '13px 15px', background: 'var(--surface-2)' }}>
                <div style={{ font: '600 12px/1 var(--font-sans)' }}>Evolução no período</div>
                <div style={{ marginTop: 14, height: 150, display: 'flex', alignItems: 'flex-end', gap: 5 }}>
                  {stats.byDay.slice(-16).map((d) => {
                    const max = Math.max(...stats.byDay.map((x) => x.value), 1)
                    return (
                      <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                        <span style={{ font: '400 8px/1 var(--font-mono)', color: 'var(--text-3)' }}>{d.value}</span>
                        <div style={{ width: '100%', height: `${(d.value / max) * 100}%`, background: 'var(--primary)', borderRadius: '2px 2px 0 0' }} />
                        <span style={{ font: '400 7.5px/1 var(--font-mono)', color: 'var(--text-3)' }}>{d.label.slice(8, 10)}</span>
                      </div>
                    )
                  })}
                  {stats.byDay.length === 0 && <div style={{ font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>Sem dados no período.</div>}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="card" style={{ padding: '13px 15px', background: 'var(--surface-2)' }}>
                <div style={{ font: '600 12px/1 var(--font-sans)' }}>Por aparelho</div>
                <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <BarList items={stats.byEquipment ?? []} />
                </div>
              </div>
              <div className="card" style={{ padding: '13px 15px', background: 'var(--surface-2)' }}>
                <div style={{ font: '600 12px/1 var(--font-sans)' }}>Por sexo</div>
                <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <BarList items={stats.bySex} />
                </div>
              </div>
              <div className="card" style={{ padding: '13px 15px', background: 'var(--surface-2)' }}>
                <div style={{ font: '600 12px/1 var(--font-sans)' }}>Por convênio</div>
                <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <BarList items={stats.byInsurance} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function BarList({ items }: { items: { label: string; value: number }[] }) {
  const max = Math.max(...items.map((i) => i.value), 1)
  const colors = ['#0e6b76', '#2f8f8a', '#b8720d', '#7a6ba8', '#5f7079', '#a83226']
  return (
    <>
      {items.map((it, i) => (
        <div key={it.label} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 36px', alignItems: 'center', gap: 10 }}>
          <div style={{ font: '400 11px/1.3 var(--font-sans)', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</div>
          <div style={{ height: 15, background: 'var(--surface-3)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(it.value / max) * 100}%`, background: colors[i % colors.length] }} />
          </div>
          <div style={{ font: '500 11px/1 var(--font-mono)', textAlign: 'right' }}>{it.value}</div>
        </div>
      ))}
      {items.length === 0 && <div style={{ font: '400 11px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>Sem dados.</div>}
    </>
  )
}

function highlight(r: ReportSummary, q: string): string {
  if (!q) return '—'
  const terms = q.split(/\s+OR\s+/i).map((t) => t.trim().toLowerCase()).filter(Boolean)
  const texts = Object.values(r.content ?? {})
  for (const text of texts) {
    const lower = text.toLowerCase()
    for (const t of terms) {
      const idx = lower.indexOf(t)
      if (idx >= 0) {
        const start = Math.max(0, idx - 24)
        const end = Math.min(text.length, idx + t.length + 24)
        return `…${text.slice(start, end).trim()}…`
      }
    }
  }
  return '—'
}
